import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { endpoints } from '@shared/endpoints';
import { PermissionGroupResponse } from '../models/api/response/permissions-management.response';
import { addNewGroupRequest } from '../models/api/requests/permissions-manager.request';
import { IndividualPermissionsResponse } from '../models/api/response/individual-permissions.response';
import { Observable, of, shareReplay } from 'rxjs';
import { PlayerProfilesWithPagination } from '../../user-management/models/api/response/user-management.response';
import { SmartTableResult } from '@shared/models/smart-table.interface';
import { SummaryResponse } from '../models/api/response/summary.response';

@Injectable({
    providedIn: 'root',
})
export class PermissionsManagerService {
    constructor(private http: HttpClient) {}

    fetchPermissionsGroupsRequest(result?: SmartTableResult) {
        return this.http
            .get<PermissionGroupResponse[]>(
                endpoints.permissions.children.getPermissionsGroups(result)
            )
            .pipe(shareReplay());
    }
    fetchUsersInGroup(groupId: string, result?: SmartTableResult) {
        return this.http
            .get<PlayerProfilesWithPagination>(
                endpoints.permissions.children.getUsersInGroupUrl(
                    groupId,
                    result
                )
            )
            .pipe(shareReplay());
    }

    fetchPermissionsRequest() {
        return this.http.get<any>(
            endpoints.permissions.children.getPermissions()
        );
    }

    addNewPermissionGroup(payload: addNewGroupRequest) {
        return this.http.post<PermissionGroupResponse>(
            endpoints.permissions.children.addNewGroup(),
            payload
        );
    }
    addUsersToGroup(groupId: string, users: any) {
        return this.http.post<any>(
            endpoints.permissions.children.addUsersToGroup(groupId),
            {
                users: users,
            }
        );
    }

    removeUsersFromGroup(groupId: string, userIds: string[]) {
        return this.http.post<any>(
            endpoints.permissions.children.removeUsersFromGroup(groupId),
            userIds
        );
    }

    deleteGroup(id: string) {
        return this.http.delete<any>(
            endpoints.permissions.children.deleteGroup(id)
        );
    }

    updatePermissionGroup(payload: any) {
        return this.http.put<any>(
            endpoints.permissions.children.deleteGroup(payload.id),
            payload
        );
    }

    addPermissionsToGroup(groupId: string, permissions: any[]) {
        return this.http.post<any>(
            endpoints.permissions.children.addPermissionsToGroup(groupId),
            {
                permissions: [...permissions],
            }
        );
    }

    deletePermissionsFromGroup(groupId: string, permissionIds: string[]) {
        return this.http.delete<any>(
            endpoints.permissions.children.deletePermissionsFromGroup(groupId),
            { body: permissionIds }
        );
    }

    duplicatePermissionGroup(id: string, payload: any) {
        return this.http.post<PermissionGroupResponse>(
            endpoints.permissions.children.duplicateGroup(id),
            payload
        );
    }

    fetchIndividualPermissions(
        profileId: string,
        contextEntityType: string | null,
        contextEntityId?: string | null
    ): Observable<IndividualPermissionsResponse> {
        let body: any = {
            permissionContexts: [],
        };
        let additions: any = {};
        if (contextEntityType) {
            additions['contextEntityType'] = contextEntityType;
        }
        if (contextEntityId) {
            additions['contextEntityId'] = contextEntityId;
        }
        if (Object.keys(additions).length > 0) {
            body.permissionContexts = [additions];
        }
        return this.http.post<any>(
            endpoints.permissions.children.getIndividualPermissions(profileId),
            { ...body }
        );
    }

    assignIndividualPermission(profileId: string, permissions: any[]) {
        return this.http.post<any>(
            endpoints.permissions.children.modifyUserPermissions(profileId),
            {
                permissions: [
                    ...permissions.map((p) => {
                        return {
                            permissionId: p.id,
                            value: p.value,
                            contextEntityType: p.contextEntityType || null,
                            contextEntityId: p.contextEntityId || null,
                            expiresAt: p.expiresAt
                                ? new Date(p.expiresAt)
                                : null,
                        };
                    }),
                ],
            }
        );
    }

    fetchSummary(
        profileId: string,
        contextEntityType: string | null,
        contextEntityId: string | null
    ): Observable<SummaryResponse> {
        let body: any = {
            permissionContexts: [],
        };
        let additions: any = {};
        if (contextEntityType) {
            additions['contextEntityType'] = contextEntityType;
        }
        if (contextEntityId) {
            additions['contextEntityId'] = contextEntityId;
        }
        if (Object.keys(additions).length > 0) {
            body.permissionContexts = [additions];
        }
        return this.http.post<SummaryResponse>(
            endpoints.admin.children.getSummary(profileId),
            {
                ...body,
            }
        );
    }
}
